import { ReactNode } from "react";
import { useDialogState } from "./Hooks/useDialogState";
import { DialogProvider } from "@/lib/Components/Dialog/Providers/DialogProvider";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogSize,
  DialogTitle,
} from "@/components/catalyst/dialog";
import { cn } from "@/lib/utils";
import { XMarkIcon } from "@heroicons/react/20/solid";

export interface GenericDialogProps {
  children: ReactNode;
  isLoading?: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  onClose?: () => void;
  className?: string;
  bodyClassName?: string;
  size?: DialogSize;
  /** Fills the viewport; the body scrolls instead of the page. */
  fullScreen?: boolean;
}

export function BaseDialog({
  children,
  title,
  subtitle,
  onClose,
  className,
  bodyClassName,
  size = "4xl",
  fullScreen = false,
}: GenericDialogProps) {
  const { close, isOpen, depth, reject } = useDialogState();

  async function handleClose() {
    if (onClose) {
      onClose();
      return;
    }

    reject?.("dialog closed");
    await close();
  }

  const header = (
    <>
      {title ? <DialogTitle>{title}</DialogTitle> : null}
      {subtitle ? <DialogDescription>{subtitle}</DialogDescription> : null}
    </>
  );

  return (
    <Dialog
      size={size}
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleClose}
      as="div"
      className={className}
    >
      <DialogProvider depth={depth + 1}>
        {/* A full-screen dialog covers the backdrop, so it needs its own way out. */}
        {fullScreen ? (
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">{header}</div>
            <button
              type="button"
              aria-label="Close"
              onClick={handleClose}
              className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>
        ) : (
          header
        )}
        <DialogBody
          className={cn(
            fullScreen && "min-h-0 flex-1 overflow-y-auto",
            bodyClassName,
          )}
        >
          {children}
        </DialogBody>
      </DialogProvider>
    </Dialog>
  );
}
